import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'WindowDimensionProvider';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { DayPicker } from 'components/molecules/DayPicker';
import { DateRange } from 'react-day-picker';
import moment, { Moment } from 'moment';
import styled from 'styled-components';
import { ExperiencesAvailability, Title4 } from './styles';

interface RangeCalendarAvailabilityProps {
  experience: { loading: boolean };
  handleOnDateChange: ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
  }) => void;
  isBookable: boolean;
  isDayHighlighted: (date: Date) => boolean;
  isNarrowView: boolean;
  isRecurring: boolean;
  endDateCalendar: Moment | null;
  startDateCalendar: Moment | null;
  onNextMonthClick: (date: Moment) => void;
  onPrevMonthClick: (date: Moment) => void;
}

const RangeCalendarAvailability = ({
  endDateCalendar,
  experience,
  handleOnDateChange,
  isBookable,
  isDayHighlighted,
  isRecurring,
  startDateCalendar,
  onNextMonthClick,
  onPrevMonthClick,
  isNarrowView,
}: RangeCalendarAvailabilityProps) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();

  if (!isBookable || !isRecurring || isMobile) return null;

  if (experience.loading) {
    return <SkeletonLine />;
  }

  const handleDateChange = (range: DateRange) => {
    handleOnDateChange({
      startDate: range?.from ? moment(range?.from) : null,
      endDate: range?.to ? moment(range?.to) : null,
    });
  };

  return (
    <ExperiencesAvailability
      role="region"
      aria-label={translate('dateRangeSelector')}
    >
      <Title4
        as={'h3' as const}
        className="experience-view__experience-section-title"
      >
        {translate('avail')}
      </Title4>
      <CalendarAvailabilityWrapper>
        <DayPicker
          mode="range"
          animate
          numberOfMonths={isNarrowView ? 1 : 2}
          required
          selected={{
            from: startDateCalendar?.toDate?.(),
            to: endDateCalendar?.toDate?.(),
          }}
          min={1}
          onSelect={handleDateChange}
          modifiers={{
            disabled: (date) => !isDayHighlighted(date),
          }}
          onNextClick={(date) => onNextMonthClick(moment(date))}
          onPrevClick={(date) => onPrevMonthClick(moment(date))}
        />
      </CalendarAvailabilityWrapper>
    </ExperiencesAvailability>
  );
};

const CalendarAvailabilityWrapper = styled.div`
  & > div {
    border-radius: 12px;
    border: 1px solid var(--way-palette-gray-50);
    box-shadow: none !important;
  }
`;

export default RangeCalendarAvailability;
